import { I18n } from '@app/i18n/strings-keys';
import {
  stringCaseInsensitiveComparator,
  getGroupStrCaseInsensitiveComparator,
} from '@app/shared/common';
import { generateDecimalRegexp } from '@app/shared/constants';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import { COMMON_WARN_ICON_COLUMN } from '@app/shared/constants/common-warn-grid-setting';
import { IBaseEntityType, ListColumn } from '@app/shared/types';
import {
  CELL_FILTER,
  CELL_RENDERER,
  ENTITY_TYPES,
} from '@app/shared/types/enums';
import { INVENTORY_COUNT_ITEM_STATUS_VALUE } from '@app/shared/types/enums/inventory-count.enum';
import {
  IBaseFormService,
  IXVIListColumn,
  SORT_ORDERS,
} from '@app/shared/types/ui';
import {
  buildDecimalPlaceholder,
  buildMaxQuantityValue,
  getExtraQuickFilterText,
} from '@app/shared/utils';
import { ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { get } from 'lodash';

const inventoryCountPrecision = 3;
const inventoryCountMaxValue = buildMaxQuantityValue(
  6,
  inventoryCountPrecision,
);

export const inventoryCountTreeColumns = (
  formService: IBaseFormService<ENTITY_TYPES.INVENTORY_COUNT>,
): IXVIListColumn[] => {
  return [
    COMMON_WARN_ICON_COLUMN,
    {
      path: 'reporting_category_major.name',
      filterParams: {
        quickFilter: formService.isCategoryListView(),
      },
    },
    {
      path: 'reporting_category_minor.name',
      filterParams: {
        quickFilter: formService.isCategoryListView(),
      },
    },
    {
      path: 'name',
      title: I18n.EntityNames.InventoryItem,
      sortingOrder: [SORT_ORDERS.ASC, SORT_ORDERS.DESC],
      cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
      cellRendererParams: {
        // turn off the row count
        suppressCount: true,
      },
      defaultView: true,
      sortable: true,
      sort: SORT_ORDERS.ASC,
      showRowGroup: true,
      filterParams: {
        quickFilter: true,
      },
      comparator: getGroupStrCaseInsensitiveComparator(),
      getQuickFilterTextForTreeView: (params: IBaseEntityType): string =>
        getExtraQuickFilterText({
          name: get(params, 'name'),
          extraValues: get(params, 'upc_codes', []),
        }),
      minWidth: 220,
    },
    {
      path: 'description',
      title: I18n.Common.MeasureName,
      defaultView: true,
      valueFormatter: formService.descriptionValueFormatter.bind(formService),
      minWidth: 120,
    },
    {
      path: 'count',
      title: I18n.Common.Count,
      defaultView: true,
      cellRendererSelector:
        formService.countCellRendererSelector.bind(formService),
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.numberValueFormatter(params, false),
      editable: true,
      cellClassRules: formService.getQuantityCellClassRules([
        INVENTORY_COUNT_ITEM_STATUS_VALUE.Open,
      ]),
      cellRendererParams: (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        params: ICellRendererParams,
      ): ListColumn['cellRendererParams'] => {
        return {
          defaultValue: null,
          placeholder: buildDecimalPlaceholder(inventoryCountPrecision),
          precision: inventoryCountPrecision,
          disabled: !formService.canEdit,
          disableKeyUpFinishEditing: true,
          min: 0,
          max: inventoryCountMaxValue,
          acceptableInputRegex: generateDecimalRegexp(
            inventoryCountPrecision,
            true,
          ),
          usePreferenceDecimalPrecision: {
            overrideMax: true,
            overrideMin: true,
          },
          artificialInputCheck: true,
        };
      },
      ...COLUMN_ALIGN_RIGHT,
      minWidth: 120,
    },
    {
      path: 'inventory_item_cost',
      title: I18n.Common.InventoryItemCost,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.numberValueFormatter(params, true),
      minWidth: 140,
    },
    {
      path: 'system_count',
      title: I18n.Common.QOH,
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.numberValueFormatter(params, false),
    },
    {
      path: 'manager_count',
      title: I18n.Common.ManagerCount,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.showFormatterOfCount(params, false),
    },
    {
      path: 'uom',
      title: I18n.Common.UOM,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.showUOMColumnValue(params),
    },
    {
      path: 'counted_value',
      title: I18n.Forms.InventoryCountForm.CountedValue,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.showFormatterOfCount(params, true),
    },
    {
      path: 'extended_value',
      title: I18n.Forms.InventoryCountForm.ExtendedValue,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.showFormatterOfCount(params, true),
    },
    {
      path: 'variance_cost',
      title: I18n.Forms.InventoryCountForm.VarianceCost,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.numberValueFormatter(params, true),
    },
    {
      path: 'variance_quantity',
      title: I18n.Forms.InventoryCountForm.VarianceQuantity,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        formService.numberValueFormatter(params, false),
    },
  ];
};

export const inventoryCountListColumns = (
  formService: IBaseFormService<ENTITY_TYPES.INVENTORY_COUNT>,
): IXVIListColumn[] => {
  return [
    {
      path: 'name',
      title: I18n.EntityNames.InventoryItem,
      cellRendererParams: {
        suppressCount: true,
      },
      defaultView: true,
      sortable: true,
      sortingOrder: [SORT_ORDERS.ASC, SORT_ORDERS.DESC],
      sort: SORT_ORDERS.ASC,
      showRowGroup: true,
      filterParams: {
        quickFilter: true,
      },
      valueFormatter: (params): string =>
        formService.isNotLeaf(params) ? params.value : '',
      comparator: stringCaseInsensitiveComparator,
      minWidth: 180,
      getQuickFilterText: (params: IBaseEntityType): string =>
        getExtraQuickFilterText({
          name: get(params, 'value'),
          extraValues: get(params, 'data.upc_codes', []),
        }),
    },
    {
      path: 'reporting_category_major.entity_id',
      labelPath: 'reporting_category_major.name',
      title: I18n.Forms.GridForm.MajorCategory,
      canSelected: true,
      selected: false,
      minWidth: 120,
      sortable: true,
      hide: true,
      filterType: CELL_FILTER.entity,
      valueFormatter: (params): string =>
        formService.isNotLeaf(params) ? params.value : '',
      comparator: stringCaseInsensitiveComparator,
      filterParams: {
        quickFilter: true,
        values: formService.majorItems$,
        filterOptions: ['containsAny'],
        suppressSelectAllControl: true,
      },
    },
    {
      path: 'reporting_category_minor.entity_id',
      labelPath: 'reporting_category_minor.name',
      title: I18n.Forms.GridForm.MinorCategory,
      canSelected: true,
      minWidth: 120,
      selected: false,
      hide: true,
      sortable: true,
      filterType: CELL_FILTER.entity,
      valueFormatter: (params): string =>
        formService.isNotLeaf(params) ? params.value : '',
      comparator: stringCaseInsensitiveComparator,
      filterParams: {
        quickFilter: true,
        values: formService.minorItems$,
        filterOptions: ['containsAny'],
        suppressSelectAllControl: true,
      },
    },
    {
      path: 'description',
      title: I18n.Common.MeasureName,
      defaultView: true,
      valueFormatter: (params): string =>
        formService.isNotLeaf(params)
          ? ''
          : formService.descriptionValueFormatter(params),
      minWidth: 120,
    },
    {
      path: 'measure.count',
      title: I18n.Common.Count,
      defaultView: true,
      cellRendererSelector: (params): ComponentSelectorResult | null =>
        formService.isNotLeaf(params)
          ? null
          : formService.countCellRendererSelector(params),
      valueFormatter: (params): string =>
        formService.isNotLeaf(params)
          ? null
          : formService.numberValueFormatter(params, false),
      editable: true,
      cellClassRules: formService.getQuantityCellClassRules([
        INVENTORY_COUNT_ITEM_STATUS_VALUE.Open,
      ]),
      cellRendererParams: (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        params: ICellRendererParams,
      ): ListColumn['cellRendererParams'] => {
        return {
          defaultValue: null,
          placeholder: buildDecimalPlaceholder(inventoryCountPrecision),
          precision: inventoryCountPrecision,
          disabled: !formService.canEdit,
          disableKeyUpFinishEditing: true,
          min: 0,
          max: inventoryCountMaxValue,
          acceptableInputRegex: generateDecimalRegexp(
            inventoryCountPrecision,
            true,
          ),
          usePreferenceDecimalPrecision: {
            overrideMax: true,
            overrideMin: true,
          },
          artificialInputCheck: true,
        };
      },
      ...COLUMN_ALIGN_RIGHT,
      minWidth: 120,
    },
    {
      path: 'inventory_item_cost',
      title: I18n.Common.InventoryItemCost,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.listViewDisplayValue(params, true, false),
      minWidth: 140,
    },
    {
      path: 'system_count',
      title: I18n.Common.QOH,
      defaultView: true,
      valueFormatter: (params): string =>
        formService.listViewDisplayValue(params, false, false),
    },
    {
      path: 'manager_count',
      title: I18n.Common.ManagerCount,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.listViewDisplayValue(params, false, true),
    },
    {
      path: 'uom',
      title: I18n.Common.UOM,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.showUOMColumnValue(params),
    },
    {
      path: 'counted_value',
      title: I18n.Forms.InventoryCountForm.CountedValue,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.listViewDisplayValue(params, true, true),
    },
    {
      path: 'extended_value',
      title: I18n.Forms.InventoryCountForm.ExtendedValue,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.listViewDisplayValue(params, true, true),
    },
    {
      path: 'variance_cost',
      title: I18n.Forms.InventoryCountForm.VarianceCost,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.listViewDisplayValue(params, true, false),
    },
    {
      path: 'variance_quantity',
      title: I18n.Forms.InventoryCountForm.VarianceQuantity,
      headerClass: 'text-wrapping',
      defaultView: true,
      valueFormatter: (params): string =>
        formService.listViewDisplayValue(params, false, false),
    },
  ];
};
